/* eslint-disable max-lines */
import React, { Component, cloneElement } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { Button } from '@wf-common/atoms'
import { isNumber } from '@wf-sl/common-utilities'
import DateRangePicker from '@wf/react-daterange-picker'

import { isValidDate, moment, parseTime, roundUpLastMinute, isValidDateOrRange } from '../../util/date'
import TopBar, { isTimeSelectionEnabled } from './datepicker-popover-topbar'
import { toMomentRange } from './moment'
import PeriodList from './period-list'
import * as customPropTypes from './prop-types'

import styles from './datepicker-popover.module.css'

const labelsProp = PropTypes.shape({
    apply: PropTypes.string,
    cancel: PropTypes.string,
    timeInputErrorMessage: PropTypes.string,
    timeToggle: PropTypes.string
})

const propTypes = {
    allDay: PropTypes.bool,
    allDayDisabled: PropTypes.bool,
    allDayTooltipContent: PropTypes.string,
    autoAllDay: PropTypes.bool,
    className: PropTypes.string,
    disableYearMonthChoiceArrows: PropTypes.bool,
    emptyTimeField: PropTypes.bool,
    initialMonth: PropTypes.number,
    initialYear: PropTypes.number,
    intl: customPropTypes.intl.isRequired,
    labels: labelsProp,
    limitPeriod: PropTypes.number,
    locale: PropTypes.string,
    max: PropTypes.instanceOf(moment),
    min: PropTypes.instanceOf(moment),
    noPopoverRemount: PropTypes.bool,
    onChange: PropTypes.func,
    onClose: PropTypes.func,
    predefinedPeriods: PropTypes.arrayOf(PropTypes.element),
    rangeSelection: PropTypes.bool,
    showCurrentMonth: PropTypes.oneOf(['first', 'last']),
    timeSelection: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), customPropTypes.dateRange])
}

const defaultProps = {
    allDay: false,
    allDayDisabled: false,
    allDayTooltipContent: '',
    autoAllDay: true,
    emptyTimeField: false,
    onChange: () => {},
    onClose: () => {},
    rangeSelection: false,
    showCurrentMonth: 'last',
    timeSelection: false,
    value: moment.range()
}

const defaultLabels = {
    apply: 'Apply',
    cancel: 'Cancel',
    timeInputErrorMessage: 'Time input format is invalid.',
    timeToggle: 'All-day'
}

/**
 * The date-picker popover component. Contains the react-daterange-picker widget,
 * the predefinedPeriods options list, footer buttons, etc. Will be rendered by
 * the <DatePicker> if its input field is clicked.
 */
class DatePickerPopover extends Component {
    constructor(props) {
        super(props)

        const { allDay, autoAllDay, intl, rangeSelection, timeSelection } = props
        const { timeFrom, timeTo } =
            (!props.autoAllDay && props.allDay) || !isValidDate(props.value?.start)
                ? this.getDefaultTimes()
                : this.getTimesFromProps(props.value, props.intl)

        this.state = {
            allDay:
                !timeSelection ||
                !isTimeSelectionEnabled({
                    allDay,
                    autoAllDay,
                    intl,
                    rangeSelection,
                    timeFrom,
                    timeTo
                }),
            selectedKey: null,
            timeFrom,
            timeFromError: null,

            timeTo,

            timeToError: null,
            // Need to mirror the value here in the state, since we need to be
            // able to select a value here that is not immediately propagated
            // to the parent in order to make apply/cancel buttons work.
            // Also, if no default date has been provided for a singe-date selection,
            // use a default date set to today or the minimum date, if provided.
            value: isValidDateOrRange(props.value) || rangeSelection ? props.value : this.getDefaultValue()
        }

        this.labels = Object.assign({}, defaultLabels, props.labels)
        this.updatePrevented = false
        this.onApply = this.onApply.bind(this)
        this.onManualDateRangeChange = this.onManualDateRangeChange.bind(this)
        this.onPredefinedPeriodSelection = this.onPredefinedPeriodSelection.bind(this)
        this.onResetTime = this.onResetTime.bind(this)
        this.lock = this.lock.bind(this)
        this.unlock = this.unlock.bind(this)
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.noPopoverRemount && nextProps.value !== this.props.value) {
            const { timeFrom, timeTo } = this.getTimesFromProps(nextProps.value, nextProps.intl)

            this.setState({
                selectedKey: null,
                timeFrom,
                timeTo,
                value: nextProps.value
            })
        }
    }

    shouldComponentUpdate() {
        return !this.updatePrevented
    }

    render() {
        const {
            className,
            disableYearMonthChoiceArrows,
            initialMonth,
            initialYear,
            intl,
            limitPeriod,
            max,
            min,
            rangeSelection,
            showCurrentMonth,
            timeSelection
        } = this.props
        const locale = intl.getLocale()
        const localeData = moment.localeData(locale) || moment.localeData()
        const roundedValue = rangeSelection ? this.roundValueToFullDays(this.state.value) : this.state.value
        const value = rangeSelection ? roundedValue : roundedValue.start
        const initialValue =
            isValidDateOrRange(value) && !(this.props.value === null && isNumber(initialYear) && isNumber(initialMonth))
                ? value
                : null

        return (
            <div>
                {timeSelection && this.renderTopBar()}
                <div className={styles['main']}>
                    <DateRangePicker
                        {...{ className, disableYearMonthChoiceArrows, limitPeriod, locale, showCurrentMonth }}
                        ref={datePicker => {
                            this.dateRangePicker = datePicker
                        }}
                        value={initialValue}
                        firstOfWeek={localeData.firstDayOfWeek()}
                        initialMonth={initialMonth}
                        initialYear={initialYear}
                        minimumDate={min && min.toDate()}
                        maximumDate={max && max.toDate()}
                        numberOfCalendars={2}
                        onSelect={this.onManualDateRangeChange}
                        onSelectStart={this.lock}
                        selectionType={rangeSelection ? 'range' : 'single'}
                        singleDateRange
                    />
                    {this.renderPredefinedPeriods()}
                </div>
                {this.renderButtonBar()}
            </div>
        )
    }

    renderTopBar() {
        const { allDay, allDayDisabled, allDayTooltipContent, autoAllDay, emptyTimeField, intl, rangeSelection } =
            this.props
        const { timeFrom, timeFromError, timeTo, timeToError, value } = this.state

        return (
            <TopBar
                allDayLabel={this.labels.timeToggle}
                onResetTime={this.onResetTime}
                onTimeFrom={timeFrom => this.setState({ timeFrom })}
                onTimeTo={timeTo => this.setState({ timeTo })}
                {...{
                    allDay,
                    allDayDisabled,
                    allDayTooltipContent,
                    autoAllDay,
                    emptyTimeField,
                    intl,
                    rangeSelection,
                    timeFrom,
                    timeFromError,
                    timeTo,
                    timeToError,
                    value
                }}
            />
        )
    }

    renderPredefinedPeriods() {
        const { predefinedPeriods } = this.props
        let { selectedKey } = this.state

        if (!predefinedPeriods) {
            return null
        }

        return (
            <PeriodList className={cx('t3sel-datepicker-predefined-periods', styles['predefined-periods'])}>
                {predefinedPeriods.map(option => {
                    const { element, selected } = this.renderPredefinedPeriodOption(option, selectedKey)
                    selectedKey = selectedKey || selected
                    return element
                })}
            </PeriodList>
        )
    }

    renderPredefinedPeriodOption(option, selectedKey) {
        const { locale } = this.props
        const { value } = this.state
        const optionValue = toMomentRange(option.props.value, locale)

        const key = option.props.title
        const onClick = () => this.onPredefinedPeriodSelection(optionValue, key)
        const selected = (!selectedKey || selectedKey === key) && this.isSameDateRange(value, optionValue)
        const props = Object.assign({}, option.props, { key, onClick, selected })

        return { element: cloneElement(option, props), selected }
    }

    renderButtonBar() {
        const { onClose } = this.props
        const { value } = this.state

        return (
            <div className={cx(styles['button-bar'], { [styles['hidden']]: this.isAutoApplying() })}>
                <Button className="t3sel-datepicker-cancel" onClick={onClose}>
                    {this.labels.cancel}
                </Button>
                <Button
                    className="t3sel-datepicker-apply"
                    primary
                    disabled={!isValidDateOrRange(value)}
                    onClick={this.onApply}
                >
                    {this.labels.apply}
                </Button>
            </div>
        )
    }

    onManualDateRangeChange(value) {
        const { emptyTimeField } = this.props
        const { locale, timeFrom, timeTo } = this.getDefaultTimes()

        this.unlock()
        this.setState({ value: toMomentRange(value, locale) })
        if (!emptyTimeField) {
            this.setState({ timeFrom, timeTo })
        }
        if (this.isAutoApplying()) {
            this.onApply()
        }
    }

    onPredefinedPeriodSelection(value, key) {
        const { emptyTimeField } = this.props
        const { timeFrom, timeTo } = this.getTimesFromProps(value, this.props.intl)

        this.setState({ selectedKey: key, value })
        if (!emptyTimeField) {
            this.setState({ timeFrom, timeTo })
        }
    }

    onResetTime(allDay) {
        const { timeFrom, timeTo } = this.getDefaultTimes()
        this.setState({ allDay, timeFrom, timeTo })
    }

    onApply() {
        const { emptyTimeField, onChange, onClose, rangeSelection, timeSelection } = this.props
        const { allDay, value } = this.state
        // work-around for tablet support: for single clicks in Rangeselection.
        const newValue = this.dateRangePicker.finishRangeSelection() || value
        const timeFrom = timeSelection || emptyTimeField ? this.state.timeFrom : '0:00'
        const timeTo = timeSelection || emptyTimeField ? this.state.timeTo : '23:59'

        const adaptedValue = rangeSelection
            ? this.applyTimesToValue(timeFrom, timeTo, newValue)
            : this.applyTimesToValue(timeFrom, timeFrom, newValue)

        if (!isValidDateOrRange(value) || !adaptedValue || !this.checkTimes(adaptedValue)) {
            return
        }

        this.setState({ timeFromError: null, timeToError: null })
        onChange(adaptedValue, allDay)
        onClose()
    }

    /**
     * @param {string} timeFromString
     * @param {string} timeToString
     * @param {MomentRange} value
     * @return {MomentRange|null}
     */
    applyTimesToValue(timeFromString, timeToString, value) {
        try {
            value = this.applyTimeToValue(timeFromString, value)
        } catch (timeFromError) {
            this.setState({ timeFromError })
            return null
        }

        try {
            value = this.applyTimeToValue(timeToString, value, true)
        } catch (timeToError) {
            this.setState({ timeToError })
            return null
        }

        return value
    }

    /**
     * @param {string} timeString
     * @param {Moment|MomentRange} value
     * @param {bool} [isEndValue]
     * @return {Moment|MomentRange|null}
     */
    applyTimeToValue(timeString, value, isEndValue = false) {
        const parsedTime = parseTime(timeString, this.labels.timeInputErrorMessage)
        const adaptedTime = isEndValue ? roundUpLastMinute(parsedTime) : parsedTime

        return isEndValue
            ? moment.range(value.start, moment(value.end).set(adaptedTime))
            : moment.range(moment(value.start).set(adaptedTime), value.end)
    }

    /**
     * If rangeSelection && timeSelection then check if start date-time < end date-time.
     * Returns false if the check fails and sets error messages in the state.
     *
     * @param {Moment|MomentRange} adaptedValue     Value (selected dates) with times already applied.
     * @return {bool}   true if everything is alright.
     */
    checkTimes(adaptedValue) {
        const { rangeSelection, timeSelection } = this.props

        if (!rangeSelection || !timeSelection) {
            return true
        }

        if (adaptedValue.start.isAfter(adaptedValue.end)) {
            this.setState({
                timeFromError: new Error('The start time should be before the end time.')
            })
            return false
        }

        return true
    }

    /**
     * If a moment-range is given it will round the range's start and end to full
     * days, so the react-daterange-picker will display the snake correctly.
     *
     * @param {MomentRange} value
     * @return {MomentRange}
     */
    roundValueToFullDays(value) {
        return moment.range(moment(value.start).startOf('day'), moment(value.end).endOf('day'))
    }

    /**
     * @param {Moment|MomentRange|null} value
     * @param {Intl} intl
     * @return {object} { timeFrom: Moment, timeTo: Moment }
     */
    getTimesFromProps(value, intl) {
        const currentYear = new Date().getFullYear()

        return {
            timeFrom: intl.formatTimeOnly(value.start.toDate().setFullYear(currentYear)),
            timeTo: intl.formatTimeOnly(value.end.toDate().setFullYear(currentYear))
        }
    }

    getDefaultValue() {
        const { locale, max, min, rangeSelection } = this.props
        const today = new Date()
        // If today is within the bounds or if no bounds have been provided, use today.
        // Otherwise, use min or max, whichever is set.
        return toMomentRange(
            (!min || today >= min) && (!max || today <= max) ? today : min || max,
            locale,
            rangeSelection
        )
    }

    /**
     * Returns the localized default values for timeFrom & timeTo.
     * @return {object} { timeFrom: string, timeTo: string }
     */
    getDefaultTimes() {
        const { emptyTimeField, intl } = this.props

        if (emptyTimeField) {
            return { timeFrom: '', timeTo: '' }
        }

        const momentFrom = moment().set({ hour: 0, minute: 0, second: 0 })
        const momentTo = moment().set({ hour: 23, minute: 59, second: 59 })

        return {
            timeFrom: intl.formatTimeOnly(momentFrom.toDate()),
            timeTo: intl.formatTimeOnly(momentTo.toDate())
        }
    }

    isAutoApplying() {
        const { rangeSelection, timeSelection } = this.props
        return !rangeSelection && !timeSelection
    }

    /**
     * @param {MomentRange|null} value1
     * @param {MomentRange|null} value2
     * @return {bool}   true if value1 equals to value2
     */
    isSameDateRange(value1, value2) {
        return isValidDateOrRange(value1) && isValidDateOrRange(value2)
            ? value1.start.isSame(value2.start, 'day') && value1.end.isSame(value2.end, 'day')
            : false
    }

    lock() {
        this.updatePrevented = true
    }

    unlock() {
        this.updatePrevented = false
    }
}

DatePickerPopover.propTypes = propTypes
DatePickerPopover.defaultProps = defaultProps

export default DatePickerPopover
